// A4 GST Tax Invoice for Accessory billing (retail + B2B sub-dealer).
//  - Dealership letterhead, TAX INVOICE bar, invoice meta
//  - Bill-To: retail customer OR sub-dealer branch
//  - Items: part no, description, HSN, qty, rate (taxable), CGST/SGST or IGST, total
//  - Totals + HSN-wise tax summary + amount in words
//  - Payment status block: PAID (method/ref) or FREE ISSUE stamp
import { fmtDate } from "@/lib/format";

function escHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

const fmtAmt = (n) => (Number(n) || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

function numToIndianWords(n) {
  n = Math.round(Number(n) || 0);
  if (n === 0) return "Zero";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const twoDigits = (x) => {
    if (x < 10) return ones[x];
    if (x < 20) return teens[x - 10];
    const t = Math.floor(x / 10);
    const o = x % 10;
    return tens[t] + (o ? " " + ones[o] : "");
  };
  const threeDigits = (x) => {
    const h = Math.floor(x / 100);
    const rest = x % 100;
    let s = "";
    if (h) s += ones[h] + " Hundred";
    if (rest) s += (h ? " " : "") + twoDigits(rest);
    return s;
  };
  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh = Math.floor(n / 100000); n %= 100000;
  const thousand = Math.floor(n / 1000); n %= 1000;
  const hundred = n;
  const parts = [];
  if (crore) parts.push(threeDigits(crore) + " Crore");
  if (lakh) parts.push(twoDigits(lakh) + " Lakh");
  if (thousand) parts.push(threeDigits(thousand) + " Thousand");
  if (hundred) parts.push(threeDigits(hundred));
  return "Rupees " + parts.join(" ").replace(/\s+/g, " ").trim() + " Only";
}

export default function buildAccessoryInvoiceHtml(invoice) {
  const inv = invoice || {};
  const d = inv.dealership || {};
  const isB2B = inv.invoice_type === "b2b_sub_dealer";
  const buyer = isB2B ? (inv.sub_dealer || {}) : null;
  const isIntra = (inv.gst_type || "intra") === "intra";
  const lines = inv.lines || [];
  const isFree = inv.collection_mode === "free_bundled";

  const itemRows = lines.map((ln, i) => {
    const qty = Number(ln.qty) || 1;
    const rate = qty > 0 ? (Number(ln.taxable) || 0) / qty : 0;
    return `<tr>
      <td class="num">${i + 1}</td>
      <td><b>${escHtml(ln.description || "")}</b><br/><span class="dim mono">${escHtml(ln.part_no || "")}</span></td>
      <td class="mono">${escHtml(ln.hsn_code || "—")}</td>
      <td class="num">${qty} NOS</td>
      <td class="num">${escHtml(fmtAmt(rate))}</td>
      ${isIntra
        ? `<td class="num">${escHtml(fmtAmt(ln.cgst))}</td><td class="num">${escHtml(fmtAmt(ln.sgst))}</td>`
        : `<td class="num">${escHtml(fmtAmt(ln.igst))}</td>`}
      <td class="num"><b>${escHtml(fmtAmt(ln.total))}</b></td>
    </tr>`;
  }).join("");

  // HSN-wise tax summary
  const taxByHsn = {};
  lines.forEach((ln) => {
    const key = (ln.hsn_code || "").trim() || "—";
    if (!taxByHsn[key]) taxByHsn[key] = { taxable: 0, cgst: 0, sgst: 0, igst: 0, rate: Number(ln.gst_pct) || 0 };
    taxByHsn[key].taxable += Number(ln.taxable) || 0;
    taxByHsn[key].cgst += Number(ln.cgst) || 0;
    taxByHsn[key].sgst += Number(ln.sgst) || 0;
    taxByHsn[key].igst += Number(ln.igst) || 0;
  });
  const taxRows = Object.entries(taxByHsn).map(([hsn, t]) => `<tr>
    <td class="mono">${escHtml(hsn)}</td>
    <td class="num">${escHtml(fmtAmt(t.taxable))}</td>
    ${isIntra
      ? `<td class="num">${(t.rate / 2).toFixed(1)}%</td><td class="num">${escHtml(fmtAmt(t.cgst))}</td>
         <td class="num">${(t.rate / 2).toFixed(1)}%</td><td class="num">${escHtml(fmtAmt(t.sgst))}</td>`
      : `<td class="num">${t.rate}%</td><td class="num">${escHtml(fmtAmt(t.igst))}</td>`}
    <td class="num"><b>${escHtml(fmtAmt(t.cgst + t.sgst + t.igst))}</b></td>
  </tr>`).join("");

  const totalQty = lines.reduce((s, ln) => s + (Number(ln.qty) || 0), 0);
  const discount = Number(inv.discount_total) || 0;

  const paymentBlock = isB2B
    ? `<div class="pay b2b">ON ACCOUNT — posted to Sub-Dealer ledger (${escHtml((buyer.name) || "—")})</div>`
    : isFree
      ? `<div class="pay free">FREE ISSUE — NO CASH COLLECTED (bundled with vehicle sale)</div>`
      : `<div class="pay paid">PAID · ${escHtml((inv.payment_method || "").toUpperCase())}${inv.receipt_no ? ` · Receipt ${escHtml(inv.receipt_no)}` : ""}${inv.payment_reference ? ` · Ref: ${escHtml(inv.payment_reference)}` : ""}</div>`;

  return `<!doctype html><html><head><title>Accessory Invoice · ${escHtml(inv.invoice_no || "")}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:'Times New Roman',serif;color:#111;margin:18px;background:#fff;max-width:820px}
  .hdr{display:flex;align-items:flex-start;gap:18px;border:2px solid #000;padding:10px 14px}
  .logo{max-height:70px;max-width:140px;object-fit:contain}
  .dealer{flex:1;line-height:1.35}
  .dealer .name{font-size:18px;font-weight:700;letter-spacing:.5px}
  .dealer .sub{font-size:11px;color:#222}
  .title-bar{border:2px solid #000;border-top:0;text-align:center;font-size:15px;letter-spacing:6px;font-weight:700;padding:6px;background:#f4f4f4}
  .meta{display:grid;grid-template-columns:1fr 1fr 1fr;border:2px solid #000;border-top:0}
  .meta > div{padding:7px 10px;font-size:11.5px;line-height:1.5}
  .meta > div + div{border-left:1px solid #000}
  .meta .lbl{font-family:'IBM Plex Mono',monospace;font-size:9px;letter-spacing:.18em;text-transform:uppercase;color:#555;display:block}
  .meta .val{font-weight:700}
  .billto{border:2px solid #000;border-top:0;padding:8px 12px;font-size:12px;line-height:1.5}
  .billto .lbl{font-family:'IBM Plex Mono',monospace;font-size:9px;letter-spacing:.18em;text-transform:uppercase;color:#555;margin-bottom:2px}
  .billto .name{font-size:14px;font-weight:700}
  table.items{width:100%;border-collapse:collapse;border:2px solid #000;border-top:0}
  table.items th,table.items td{border:1px solid #000;padding:5px 6px;font-size:10.5px;vertical-align:middle}
  table.items th{background:#eee;font-family:'IBM Plex Mono',monospace;font-size:9.5px;letter-spacing:.05em;text-transform:uppercase}
  .mono{font-family:'Courier New',monospace;font-size:10.5px}
  td.num,th.num{text-align:right;font-family:'Courier New',monospace}
  .dim{color:#555;font-size:9.5px}
  .totals-row td{font-weight:700;background:#f4f4f4}
  table.tax{width:100%;border-collapse:collapse;border:2px solid #000;border-top:0}
  table.tax th,table.tax td{border:1px solid #000;padding:4px 8px;font-size:10.5px}
  table.tax th{background:#eee;font-family:'IBM Plex Mono',monospace;font-size:9.5px;text-transform:uppercase}
  .pay{border:2px solid #000;border-top:0;padding:8px 12px;font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:.1em;font-weight:700;text-transform:uppercase}
  .pay.free{background:#e8f5e9;color:#1b5e20}
  .pay.paid{background:#e3f2fd;color:#0d47a1}
  .pay.b2b{background:#fff3e0;color:#e65100}
  .summary{display:grid;grid-template-columns:1.4fr 1fr;border:2px solid #000;border-top:0}
  .summary .left{padding:8px 12px;font-size:11px;line-height:1.6;border-right:1px solid #000}
  .summary .left .words{font-style:italic;margin-bottom:6px}
  .summary .left .words b{font-style:normal}
  .summary .right table{width:100%;border-collapse:collapse}
  .summary .right td{padding:5px 10px;font-size:11.5px;border-bottom:1px solid #ddd}
  .summary .right td:last-child{text-align:right;font-family:'Courier New',monospace}
  .summary .right tr:last-child td{border-bottom:0;font-weight:700;background:#f4f4f4;font-size:13px}
  .sig{display:grid;grid-template-columns:1fr 1fr;border:2px solid #000;border-top:0;font-size:11px}
  .sig > div{padding:10px 14px 30px;border-right:1px solid #000;min-height:80px}
  .sig > div:last-child{border-right:0;text-align:right}
  .sig .lbl{font-family:'IBM Plex Mono',monospace;font-size:9px;letter-spacing:.18em;text-transform:uppercase;color:#555}
  .sig .for{font-weight:700;margin-top:6px}
  .notes{padding:6px 12px;font-size:10.5px;border:2px solid #000;border-top:0;font-style:italic;color:#333;background:#fafafa}
  .foot{margin-top:18px;font-family:'IBM Plex Mono',monospace;font-size:9px;color:#777;text-align:center;letter-spacing:.18em;text-transform:uppercase}
  @media print{body{margin:8mm}}
</style></head><body>
<div class="hdr">
  ${d.logo_url ? `<img class="logo" src="${escHtml(d.logo_url)}" alt="logo"/>` : ""}
  <div class="dealer">
    <div class="name">${escHtml(d.name || "DEALERSHIP")}</div>
    ${d.address ? `<div class="sub">${escHtml(d.address)}</div>` : ""}
    <div class="sub">
      ${d.gstin ? `GSTIN: <b>${escHtml(d.gstin)}</b>` : ""}
      ${d.state_name ? ` &nbsp;·&nbsp; State: ${escHtml(d.state_name)} (Code ${escHtml(d.state_code || "—")})` : ""}
    </div>
    ${(d.phone || d.email) ? `<div class="sub">${[d.phone, d.email].filter(Boolean).map(escHtml).join(" · ")}</div>` : ""}
  </div>
</div>
<div class="title-bar">${isB2B ? "TAX INVOICE" : "RETAIL INVOICE"}</div>

<div class="meta">
  <div>
    <span class="lbl">Invoice No</span>
    <span class="val">${escHtml(inv.invoice_no || "—")}</span>
  </div>
  <div>
    <span class="lbl">Invoice Date</span>
    <span class="val">${escHtml(fmtDate(inv.date))}</span>
  </div>
  <div>
    <span class="lbl">Branch</span>
    <span class="val">${escHtml((inv.branch || {}).name || "—")}</span>
  </div>
</div>

<div class="billto">
  <div class="lbl">${isB2B ? "Bill To · Sub-Dealer" : "Bill To · Customer"}</div>
  <div class="name">${escHtml(isB2B ? (buyer.name || "—") : (inv.customer_name || "Walk-in Customer"))}</div>
  ${isB2B
    ? `<div>${buyer.address ? escHtml(buyer.address) : ""}${buyer.gstin ? ` &nbsp;·&nbsp; GSTIN: <b>${escHtml(buyer.gstin)}</b>` : ""}</div>`
    : `<div>${inv.customer_phone ? `Ph: ${escHtml(inv.customer_phone)}` : ""}${inv.vehicle_id && inv.vehicle_vin ? ` &nbsp;·&nbsp; Vehicle: ${escHtml(inv.vehicle_vin)}` : ""}</div>`}
</div>

<table class="items">
  <thead>
    <tr>
      <th class="num">Sl</th>
      <th>Description of Goods</th>
      <th>HSN/SAC</th>
      <th class="num">Qty</th>
      <th class="num">Rate (₹)</th>
      ${isIntra ? `<th class="num">CGST (₹)</th><th class="num">SGST (₹)</th>` : `<th class="num">IGST (₹)</th>`}
      <th class="num">Total (₹)</th>
    </tr>
  </thead>
  <tbody>
    ${itemRows || `<tr><td colspan="8" style="text-align:center;color:#888;padding:18px">No line items.</td></tr>`}
    ${discount > 0 ? `<tr><td colspan="7" style="text-align:right">Discount</td><td class="num">- ${escHtml(fmtAmt(discount))}</td></tr>` : ""}
    <tr class="totals-row">
      <td colspan="3" style="text-align:right">Grand Total</td>
      <td class="num">${totalQty} NOS</td>
      <td colspan="${isIntra ? 3 : 2}"></td>
      <td class="num">${escHtml(fmtAmt(inv.grand_total))}</td>
    </tr>
  </tbody>
</table>

${paymentBlock}

<table class="tax">
  <thead>
    <tr>
      <th>HSN</th>
      <th class="num">Taxable Value</th>
      ${isIntra
        ? `<th class="num">CGST %</th><th class="num">CGST ₹</th><th class="num">SGST %</th><th class="num">SGST ₹</th>`
        : `<th class="num">IGST %</th><th class="num">IGST ₹</th>`}
      <th class="num">Total (₹)</th>
    </tr>
  </thead>
  <tbody>${taxRows}</tbody>
</table>

<div class="summary">
  <div class="left">
    <div class="words">Amount in Words: <b>${escHtml(numToIndianWords(inv.grand_total))}</b></div>
    <div style="margin-top:6px;font-size:10px;color:#444">
      Declaration: We declare that this invoice shows the actual price of the goods
      described and that all particulars are true and correct.
      ${isFree ? "<br/><b>Note:</b> Goods issued free of charge as part of vehicle delivery; GST accounted at full transaction value." : ""}
    </div>
  </div>
  <div class="right">
    <table>
      <tr><td>Taxable Amount</td><td>${escHtml(fmtAmt(inv.subtotal))}</td></tr>
      ${isIntra
        ? `<tr><td>CGST</td><td>${escHtml(fmtAmt(inv.cgst_total))}</td></tr>
           <tr><td>SGST</td><td>${escHtml(fmtAmt(inv.sgst_total))}</td></tr>`
        : `<tr><td>IGST</td><td>${escHtml(fmtAmt(inv.igst_total))}</td></tr>`}
      ${discount > 0 ? `<tr><td>Discount</td><td>- ${escHtml(fmtAmt(discount))}</td></tr>` : ""}
      <tr><td>Grand Total (₹)</td><td>${escHtml(fmtAmt(inv.grand_total))}</td></tr>
      ${isFree ? `<tr><td>Amount Collected</td><td>0.00</td></tr>` : ""}
    </table>
  </div>
</div>

${inv.notes ? `<div class="notes">Notes: ${escHtml(inv.notes)}</div>` : ""}

<div class="sig">
  <div>
    <div class="lbl">Receiver's Signature</div>
    <div style="margin-top:6px;font-size:10px;color:#555">Goods received in good condition.</div>
  </div>
  <div>
    <div class="for">For ${escHtml(d.name || "DEALERSHIP")}</div>
    <div class="lbl" style="margin-top:40px">Authorised Signatory</div>
  </div>
</div>

<div class="foot">Computer-generated invoice · ${escHtml(new Date().toLocaleString())}</div>
<script>
  window.addEventListener('load', function () {
    var imgs = Array.from(document.images || []);
    var pending = imgs.filter(function (im) { return !im.complete; });
    if (pending.length === 0) { setTimeout(function () { window.print(); }, 200); return; }
    var left = pending.length;
    pending.forEach(function (im) {
      im.addEventListener('load', function () { if (--left === 0) setTimeout(function () { window.print(); }, 150); });
      im.addEventListener('error', function () { if (--left === 0) setTimeout(function () { window.print(); }, 150); });
    });
  });
</script>
</body></html>`;
}
